import { dayFrom, weekdayOf } from './week'

/**
 * How something repeats: which days, how often, until when.
 *
 * Controlled and used in two places — while an appointment is being written down, and at one that
 * already exists — so that a repetition is set the same way whichever end somebody comes from.
 *
 * The weekdays are **pressable chips and not checkboxes**. Seven native boxes in a row is the one
 * shape that makes any screen look like a settings dialog from 2003, and the rest of MyLib does not
 * have a single one: a tag, a subject and a filter are all chips. This is the same thing.
 */
export interface Repetition {
  readonly weekdays: readonly string[]
  readonly everyWeeks: number
  readonly until: string
}

export interface RepeatFieldsProps {
  readonly value: Repetition
  readonly onChange: (repetition: Repetition) => void
}

export const WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const

export function RepeatFields({ value, onChange }: RepeatFieldsProps) {
  return (
    <div className="repeats">
      <div className="repeats__days" role="group" aria-label="On which days">
        {WEEKDAYS.map((day) => {
          const on = value.weekdays.includes(day)
          return (
            <button
              type="button"
              key={day}
              className="chip"
              aria-pressed={on}
              onClick={() => {
                onChange({
                  ...value,
                  weekdays: on
                    ? value.weekdays.filter((one) => one !== day)
                    : [...value.weekdays, day],
                })
              }}
            >
              {weekdayOf(dayOfWeekday(day))}
            </button>
          )
        })}
      </div>

      <div className="repeats__how">
        <div className="appointment-detail__field">
          <label className="appointment-detail__label" htmlFor="repeats-every">
            Every
          </label>
          <select
            id="repeats-every"
            value={String(value.everyWeeks)}
            onChange={(event) => {
              onChange({ ...value, everyWeeks: Number(event.target.value) })
            }}
          >
            <option value="1">week</option>
            <option value="2">second week</option>
            <option value="3">third week</option>
            <option value="4">fourth week</option>
          </select>
        </div>
        <div className="appointment-detail__field">
          <label className="appointment-detail__label" htmlFor="repeats-until">
            Until
          </label>
          <input
            id="repeats-until"
            type="date"
            className="appointment-detail__text"
            value={value.until}
            onChange={(event) => {
              onChange({ ...value, until: event.target.value })
            }}
          />
        </div>
      </div>
    </div>
  )
}

/** The weekday this appointment already falls on — the one a repetition starts out set to. */
export function weekdayOfDay(day: string): string {
  const at = new Date(`${day}T12:00:00Z`)
  return WEEKDAYS[(at.getUTCDay() + 6) % 7] ?? 'MONDAY'
}

/** A term's worth ahead, so nobody has to work a date out to get started. */
export function defaultUntil(day: string): string {
  return dayFrom(day, 70)
}

/** Any day that is that weekday, so its name comes out of the browser's own words. */
function dayOfWeekday(weekday: string): string {
  // 2026-08-17 is a Monday; the rest of the week follows it.
  return dayFrom('2026-08-17', WEEKDAYS.indexOf(weekday as (typeof WEEKDAYS)[number]))
}
